/**
 * Admin Conversations API client.
 * Calls GET /admin/conversations, GET /admin/conversations/:id.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type ConversationType = "h2h" | "h2a" | "group";

export type AdminConversation = {
  readonly id: string;
  readonly type: ConversationType;
  readonly member_count: number;
  readonly last_message_at: string | null;
  readonly frozen: boolean;
  readonly terminated: boolean;
  readonly created_at: string;
};

export type ConversationMember = {
  readonly member_type: string;
  readonly display_name: string;
  readonly unread_count: number;
  readonly muted: boolean;
  readonly pinned: boolean;
  readonly hidden: boolean;
  readonly joined_at: string;
};

export type MessageStatByType = {
  readonly content_type: string;
  readonly count: number;
};

export type MessageStatBySender = {
  readonly sender_type: string;
  readonly count: number;
};

export type MessageStats = {
  readonly total: number;
  readonly by_type: ReadonlyArray<MessageStatByType>;
  readonly by_sender: ReadonlyArray<MessageStatBySender>;
  readonly recalled: number;
};

export type ConversationDetail = {
  readonly conversation: AdminConversation;
  readonly members: ReadonlyArray<ConversationMember>;
  readonly message_stats: MessageStats;
};

type ListConversationsParams = {
  readonly q?: string;
  readonly type?: ConversationType;
  readonly frozen?: boolean;
  readonly terminated?: boolean;
  readonly limit?: number;
  readonly offset?: number;
};

type ListConversationsResponse = {
  readonly items: AdminConversation[];
  readonly total: number;
};

// ─── API Calls ───

export async function fetchConversations(
  params: ListConversationsParams,
): Promise<ListConversationsResponse> {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.type) sp.set("type", params.type);
  if (params.frozen !== undefined) sp.set("frozen", String(params.frozen));
  if (params.terminated !== undefined) {
    sp.set("terminated", String(params.terminated));
  }
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  const res = await apiFetch<{ data: ListConversationsResponse }>(
    qs ? `/admin/conversations?${qs}` : "/admin/conversations",
  );
  return res.data;
}

export async function fetchConversationDetail(
  id: string,
): Promise<ConversationDetail> {
  const res = await apiFetch<{ data: ConversationDetail }>(
    `/admin/conversations/${id}`,
  );
  return res.data;
}
